import { useState, useEffect } from 'react';
import { worksheetService, quotesService } from '../../services/api';
import './MaterialsList.css';

const SECTIONS = [
  { key: 'bring', title: 'Bring', hint: 'What we already have' },
  { key: 'buy', title: 'Buy', hint: 'From the shops' },
  { key: 'hire', title: 'Hire', hint: 'From the hire place' }
];
const NO_SUPPLIER = 'No supplier yet';
const fmtQty = (l) => {
  const v = Number(l.qty) || 0;
  const n = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
  return l.unit ? `${n} ${l.unit}` : (v === 1 ? '' : `× ${n}`);
};

/** Items of one section, grouped by supplier (A–Z, "No supplier yet" last). Bring is not grouped. */
function groupFor(section, items) {
  if (section === 'bring') return [{ supplier: null, items }];
  const map = new Map();
  for (const l of items) {
    const k = (l.supplier || '').trim() || NO_SUPPLIER;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(l);
  }
  return [...map.entries()].sort(([a], [b]) => (a === NO_SUPPLIER) - (b === NO_SUPPLIER) || a.localeCompare(b)).map(([supplier, list]) => ({ supplier, items: list }));
}

/**
 * One item: a big tick box (ticking saves at once — used on a phone in the
 * shop), the description and amount, a note that saves when you leave it, and
 * a small "Move to" for changing section.
 */
function Item({ line, onSave, onRemove }) {
  const [note, setNote] = useState(line.note || '');
  const [supplier, setSupplier] = useState(line.supplier || '');
  useEffect(() => { setNote(line.note || ''); setSupplier(line.supplier || ''); }, [line.note, line.supplier]);
  return (
    <li className={`ml-item ${line.checked ? 'done' : ''}`}>
      <label className="ml-tick">
        <input type="checkbox" checked={line.checked} onChange={(e) => onSave(line.id, { checked: e.target.checked })} aria-label={`Got ${line.description}`} />
        <span className="ml-desc">{line.description}</span>
        <span className="ml-qty">{fmtQty(line)}</span>
      </label>
      <div className="ml-extra">
        <input className="ml-note" aria-label={`Note for ${line.description}`} placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (line.note || '') && onSave(line.id, { note })} />
        {line.list_section !== 'bring' && (
          <input className="ml-supplier" aria-label={`Supplier for ${line.description}`} placeholder="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} onBlur={() => supplier !== (line.supplier || '') && onSave(line.id, { supplier })} />
        )}
        <select className="ml-move" aria-label={`Move ${line.description}`} value={line.list_section} onChange={(e) => onSave(line.id, { list_section: e.target.value })} title="Move to another section">
          <option value="bring">Bring</option><option value="buy">Buy</option><option value="hire">Hire</option>
        </select>
        <button type="button" className="ml-remove" onClick={() => onRemove(line)} title={line.category_id ? 'Take off the list (it stays in the costings)' : 'Remove this item'} aria-label={`Remove ${line.description} from the list`}>×</button>
      </div>
    </li>
  );
}

/**
 * The materials list for a saved quote (backlog #774): what to bring, what to
 * buy and from where, what to hire and from whom. It fills itself from the
 * quote's costings (a line marked Bring / Buy / Hire); list-only items added
 * here cost nothing. Print list opens the PDF. Used on the quote and on the job.
 */
export default function MaterialsList({ quoteId, onChange = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // null | { section, description, qty, supplier, note }
  const [kitId, setKitId] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await worksheetService.get(quoteId);
    if (r.success) { setData(r); setNotes(r.materials_notes || ''); setError(''); } else setError(r.error || 'Could not load the list');
  }
  useEffect(() => { setData(null); setForm(null); load(); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <p className="text-muted">{error || 'Loading…'}</p>;

  const lines = data.lines.filter((l) => l.list_section);
  const done = lines.filter((l) => l.checked).length;

  async function save(id, patch) {
    setError('');
    // Tick boxes answer at once; the server confirms.
    setData((p) => ({ ...p, lines: p.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
    const r = await worksheetService.updateLine(id, patch);
    if (r.success) { setData((p) => ({ ...p, lines: p.lines.map((l) => (l.id === id ? r.line : l)), summary: r.summary })); onChange?.(); } else { setError(r.error || 'Could not save that'); load(); }
  }
  async function remove(line) {
    setError('');
    // A costed line stays in the costings; a list-only item is deleted.
    const r = line.category_id ? await worksheetService.updateLine(line.id, { list_section: null }) : await worksheetService.deleteLine(line.id);
    if (r.success) { await load(); onChange?.(); } else setError(r.error || 'Could not remove that');
  }
  async function submitAdd(e) {
    e.preventDefault();
    if (!form.description.trim()) { setError('Say what it is'); return; }
    setBusy(true);
    const r = await worksheetService.addLine(quoteId, {
      description: form.description, qty: form.qty === '' ? 1 : Number(form.qty), list_section: form.section, supplier: form.section === 'bring' ? '' : form.supplier, note: form.note
    });
    setBusy(false);
    if (r.success) { setData((p) => ({ ...p, lines: [...p.lines, r.line] })); setForm(null); onChange?.(); } else setError(r.error || 'Could not add that');
  }
  async function addKit() {
    const kit = data.equipment.find((k) => String(k.id) === kitId);
    if (!kit) return;
    setBusy(true);
    const r = await worksheetService.addLine(quoteId, { description: kit.name, equipment_id: kit.id, list_section: 'bring' }); // list-only: no category, no cost
    setBusy(false);
    if (r.success) { setData((p) => ({ ...p, lines: [...p.lines, r.line] })); setKitId(''); onChange?.(); } else setError(r.error || 'Could not add that');
  }
  async function saveNotes() {
    if (notes === (data.materials_notes || '')) return;
    const r = await worksheetService.saveNotes(quoteId, { materials_notes: notes });
    if (r.success) setData((p) => ({ ...p, materials_notes: r.materials_notes })); else setError(r.error || 'Could not save the notes');
  }

  const kits = data.equipment;
  return (
    <div className="ml">
      <div className="ml-head">
        <div>
          <strong>{lines.length ? `${done} of ${lines.length} ticked` : 'Nothing on the list yet'}</strong>
          <span className="text-muted ml-sub"> · fills itself from the costings</span>
        </div>
        <div className="ml-head-actions">
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setForm({ section: 'buy', description: '', qty: '1', supplier: '', note: '' })} disabled={!!form}>+ Add item</button>
          <a className="btn btn-sm btn-outline" href={quotesService.materialsPdfUrl(quoteId)} target="_blank" rel="noopener noreferrer" title="Opens the printable list">Print list</a>
        </div>
      </div>

      {form && (
        <form className="ml-add" onSubmit={submitAdd}>
          <label>Section
            <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}>
              <option value="bring">Bring</option><option value="buy">Buy</option><option value="hire">Hire</option>
            </select>
          </label>
          <label className="ml-add-wide">What <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Drop sheets" autoFocus /></label>
          <label>Qty <input type="number" min="0" step="any" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></label>
          {form.section !== 'bring' && <label>Supplier <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="e.g. Bunnings Helensvale" /></label>}
          <label className="ml-add-wide">Note <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="optional" /></label>
          <div className="ml-add-actions">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setForm(null)}>Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Add</button>
          </div>
        </form>
      )}

      {SECTIONS.map((sec) => {
        const items = lines.filter((l) => l.list_section === sec.key);
        if (!items.length) return null;
        return (
          <section key={sec.key} className="ml-section">
            <h3>{sec.title} <span className="text-muted">{sec.hint}</span></h3>
            {groupFor(sec.key, items).map((g) => (
              <div key={g.supplier || 'all'} className="ml-group">
                {g.supplier && <h4>{sec.key === 'buy' ? 'Buy from' : 'Hire from'} {g.supplier === NO_SUPPLIER ? <em>{NO_SUPPLIER.toLowerCase()}</em> : g.supplier}</h4>}
                <ul>{g.items.map((l) => <Item key={l.id} line={l} onSave={save} onRemove={remove} />)}</ul>
              </div>
            ))}
          </section>
        );
      })}

      <div className="ml-kit">
        <select aria-label="Add our gear" value={kitId} onChange={(e) => setKitId(e.target.value)}>
          <option value="">+ From equipment register…</option>
          {kits.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        {kitId && <button type="button" className="btn btn-sm btn-outline" onClick={addKit} disabled={busy}>Add to Bring</button>}
      </div>

      {error && <p className="form-error">{error}</p>}
      <div className="form-group ml-notes">
        <label htmlFor={`ml_notes_${quoteId}`}>Materials notes</label>
        <textarea id={`ml_notes_${quoteId}`} rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Printed at the bottom of the list — saved when you click away" />
      </div>
    </div>
  );
}
